import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { useProvidedAppToken } from "lib/providers/ApiTokenProvider";
import { ProHelperProvider } from "lib/providers/ProHelper";
import { RecordViewerProvider } from "lib/providers/RecordViewer";
import { BidModuleCustomHeader } from "../BidModuleExternalHeader";
import { PublicBidNoBidReport } from "./PublicBidNoBidReport";

type Props = {
  bidReportId: string;
};

/**
 * Public page for a shared bid / no-bid report (/shared/bid_reports/:id).
 * Rendered bare via the `noShell` SPA layout, with a log-in / sign-up header
 * for logged-out visitors.
 */
export function SharedBidReportView({ bidReportId }: Props) {
  // An app token is only issued to logged-in users, so its absence marks a guest.
  // We key off the token (resolved synchronously via Suspense above this point)
  // rather than useCurrentUser() because the currentUser query is auth-guarded —
  // on this public route it would error and be Sentry-reported for every guest.
  const isGuest = !useProvidedAppToken();

  return (
    <Page>
      {isGuest && (
        <GuestHeader>
          <BidModuleCustomHeader />
        </GuestHeader>
      )}
      <ProHelperProvider>
        <RecordViewerProvider>
          <PublicBidNoBidReport bidReportId={bidReportId} />
        </RecordViewerProvider>
      </ProHelperProvider>
    </Page>
  );
}

const Page = styled(Flex)({
  flexDirection: "column",
  flex: 1,
});

const GuestHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: "12px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));
